import React from 'react'
import { Button, Form, Input, Modal, message } from 'antd'
import { LockOutlined, UserOutlined } from '@ant-design/icons'
import { observer } from 'mobx-react'
import { login } from '@/servers/user'
import { processLogin, updateStorageUserInfo } from '@/business/login'
import { $t } from '@/i18n/intl'
import { md5 } from '@/utils/md5'
import { getStores } from '@/stores'
import logo from '@/components/entryLogo/logo.png'
import styles from './index.module.scss'

export type LoginFormValues = {
  account: string
  password: string
}

const LoginModal: React.FC = () => {
  const { userStore } = getStores()
  const [submitting, setSubmitting] = React.useState(false)

  const handleSubmit = async (values: LoginFormValues) => {
    setSubmitting(true)
    try {
      const res = await login({
        account: values.account.trim(),
        password: md5(values.password.trim())
      })

      if (res.code !== 0) {
        res.message && message.error(res.message)
        return
      }
      if (!res.data?.token) {
        message.error($t('login.failed'))
        return
      }
      // 登录成功后立即写入缓存，后续公司列表等接口才能携带 token
      userStore.commitUpdateAction(s => {
        s.userInfo = res.data
      })
      updateStorageUserInfo({ userInfo: res.data })

      const success = await processLogin(res.data)
      if (!success) {
        return
      }
      message.success($t('login.success'))
    } catch {
      message.error($t('login.failed'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      open={userStore.showLoginModal}
      footer={null}
      closable={false}
      maskClosable={false}
      centered
      width={440}
      destroyOnClose
      className={styles.modal}
      styles={{
        mask: {
          backdropFilter: 'blur(6px)',
          background: 'rgba(15, 23, 42, 0.35)'
        }
      }}
    >
      <div className={styles.panel}>
        <div className={styles.header}>
          <img className={styles.logo} src={logo} alt="AIGC" />
          {/* <p className={styles.subtitle}>{$t('demo.main.logoText')}</p> */}
        </div>

        <Form<LoginFormValues>
          layout="vertical"
          className={styles.form}
          requiredMark={false}
          onFinish={handleSubmit}
        >
          <Form.Item
            name="account"
            label={$t('login.account')}
            rules={[{ required: true, message: $t('login.account_required') }]}
          >
            <Input
              prefix={<UserOutlined className={styles.inputIcon} />}
              placeholder={$t('login.account_placeholder')}
              autoComplete="username"
            />
          </Form.Item>
          <Form.Item
            name="password"
            label={$t('login.password')}
            rules={[{ required: true, message: $t('login.password_required') }]}
          >
            <Input.Password
              prefix={<LockOutlined className={styles.inputIcon} />}
              placeholder={$t('login.password_placeholder')}
              autoComplete="current-password"
            />
          </Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            block
            loading={submitting}
            className={styles.submit}
          >
            {$t('login.submit')}
          </Button>
        </Form>
      </div>
    </Modal>
  )
}

export default observer(LoginModal)
